"use client"
import { ChevronUpDown } from "@medusajs/icons"
import { Listbox, Transition } from "@headlessui/react"
import { Fragment } from "react"
import { clx } from "@medusajs/ui"
import {
  SelectHTMLAttributes,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from "react"
import { useTranslate } from "@tolgee/react"

export type ProvinceSelectProps = {
  placeholder?: string
  errors?: Record<string, unknown>
  touched?: Record<string, unknown>
  label?: string
  provinces?: ReadonlyArray<{ value: string; label: string }>
} & SelectHTMLAttributes<HTMLSelectElement>

const ProvinceSelect = forwardRef<HTMLSelectElement, ProvinceSelectProps>(
  (
    { placeholder = "Select...", defaultValue, className, provinces, ...props },
    ref
  ) => {
    const { t } = useTranslate()
    const innerRef = useRef<HTMLSelectElement>(null)

    useImperativeHandle<HTMLSelectElement | null, HTMLSelectElement | null>(
      ref,
      () => innerRef.current
    )

    const handleSelect = (value: string) => {
      props.onChange?.({
        target: {
          name: props.name,
          value,
        },
      } as React.ChangeEvent<HTMLSelectElement>)
    }

    return (
      <label className="text-xs font-medium text-[#364153]">
        {props.label && <p className="mb-2">{props.required && <span className="text-red-500 ml-1">*</span>}{props.label}</p> }
        <Listbox onChange={handleSelect} value={props.value ?? null}>
          <div className="relative">
            <Listbox.Button
              className={clx(
                "relative w-full h-[38px] flex justify-between items-center px-[13px] bg-white text-left cursor-default focus:outline-none border border-[#d1d5dc] rounded-[4px] focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-gray-300 focus-visible:ring-offset-2 focus-visible:border-gray-300 text-[14px]",
                className
              )}
              data-testid="province-select"
            >
              {({ open }) => (
                <>
                  <span className="block truncate">
                    {provinces?.find(province => province.value === props.value)?.label ||
                     (placeholder === "Select..." ? t("native_select.select") : placeholder)}
                  </span>
                  <ChevronUpDown
                    className={clx("transition-rotate duration-200", {
                      "transform rotate-180": open,
                    })}
                  />
                </>
              )}
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className="absolute z-20 w-full overflow-auto text-small-regular bg-white border rounded-lg border-top-0 max-h-60 focus:outline-none sm:text-sm"
                data-testid="province-options"
              >
                {provinces?.map(({ value, label }, index) => (
                  <Listbox.Option
                    key={index}
                    value={value}
                    className="cursor-default select-none relative pl-6 pr-10 hover:bg-gray-50 py-4 border-b"
                    data-testid="province-option"
                  >
                    {label}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </Listbox>
        <div className="hidden">
          <select
            ref={innerRef}
            defaultValue={defaultValue}
            {...props}
            className={clx(
              "hidden w-full h-12 items-center bg-component-secondary"
            )}
          >
            {provinces?.map(({ value, label }, index) => (
              <option key={index} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </label>
    )
  }
)

ProvinceSelect.displayName = "ProvinceSelect"

export default ProvinceSelect
